<template>
  <div>
    <div v-if="chartData?.length" class="chart-container">
      <LineChart :data="processedChartData" :options="chartOptions" />
    </div>
    <div
      v-if="delta !== undefined && delta !== null"
      class="d-flex align-center justify-end delta-indicator"
      :class="[deltaDisplay.cssClass]"
    >
      <span v-html="deltaDisplay.arrow"></span>
      <span class="delta-text">{{ deltaDisplay.text }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, type PropType } from "vue";
import LineChart from "@/components/LineChart.vue";
import type { ChartData, ChartOptions, ScriptableContext } from "chart.js";
import { getFormattedDateTime } from "@/_helpers/date";
import { DoughnutChartColors } from "@/constants/charts.ts";

import type { WidgetTicketChartItem } from "@/_store/dashboard/dashboard.module.ts";
import { useTheme } from "vuetify";
import { useI18n } from "vue-i18n";

const createGradient = (context: ScriptableContext<"line">) => {
  const chart = context.chart;
  const { ctx, chartArea } = chart;
  if (!chartArea) {
    return null;
  }
  const gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
  gradient.addColorStop(0, DoughnutChartColors.PINK);
  gradient.addColorStop(1, DoughnutChartColors.BLUE);
  return gradient;
};

export default defineComponent({
  components: {
    LineChart,
  },
  props: {
    chartData: {
      type: Array as PropType<WidgetTicketChartItem[]>,
      required: true,
    },
    delta: {
      type: Number,
    },
  },
  setup(props) {
    const theme = useTheme();
    const i18n = useI18n();
    const chartOptions: ChartOptions<"line"> = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            display: false,
          },
          grid: {
            display: false,
          },
        },
        y: {
          min: 0,
          grid: {
            color: theme.current.value.colors["indigo-faint"],
          },
          ticks: {
            precision: 0,
            color: theme.current.value.colors["indigo-light"],
          },
        },
      },
    };

    const deltaDisplay = computed(() => {
      const delta = props.delta ?? 0;

      if (delta > 0) {
        return {
          arrow: "&#8593;",
          cssClass: "text-green-dark",
          text: `${Math.abs(delta)}% ${i18n.t("coroImpact.fromLastMonth")}`,
        };
      }
      if (delta < 0) {
        return {
          arrow: "&#8595;",
          value: Math.abs(delta),
          cssClass: "text-red-dark",
          text: `${Math.abs(delta)}% ${i18n.t("coroImpact.fromLastMonth")}`,
        };
      }
      return {
        arrow: "",
        cssClass: "text-indigo-medium",
        text: i18n.t("coroImpact.noChangeFromLastMonth"),
      };
    });

    const processedChartData = computed<ChartData<"line">>(() => {
      if (!props.chartData || props.chartData.length === 0) {
        return { labels: [], datasets: [] };
      }
      const sortedData = [...props.chartData].sort((a, b) => a.date - b.date);
      const labels = sortedData.map((item) => getFormattedDateTime(item.date, "l"));
      const data = sortedData.map((item) => item.tickets);

      return {
        labels,
        datasets: [
          {
            label: i18n.t("reports.executiveSummaryReport.tickets.total"),
            tension: 0.4,
            data,
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: "white",
            pointHoverBorderColor: createGradient,
            borderColor: createGradient,
            pointBackgroundColor: createGradient,
          },
        ],
      } as ChartData<"line">;
    });

    return {
      processedChartData,
      chartOptions,
      deltaDisplay,
    };
  },
});
</script>

<style scoped lang="scss">
.chart-container {
  max-height: 70px;
}
</style>
